import { Button, Input } from '@zyxantech/tux'
import { CheckCircle2, Mail } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForgotPasswordMutation } from '@/api/auth.api'
import { ROUTES } from '@/constants/routes'

export function ForgotPasswordPage() {
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    // The endpoint always succeeds (no account enumeration); show the same
    // confirmation regardless of whether the email exists.
    try {
      await forgotPassword({ email }).unwrap()
    } catch {
      // Ignore — confirmation is intentionally identical on error.
    }
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <div className="w-full">
        <CheckCircle2 size={28} className="text-success" />
        <h1 className="mt-3 text-xl font-semibold text-text-primary">Check your email</h1>
        <p className="mt-2 text-[13px] leading-relaxed text-text-secondary">
          If an account exists for <span className="font-mono text-text-primary">{email}</span>, a
          reset code has been sent. Enter it on the next screen to set a new password.
        </p>
        <Button
          variant="primary"
          className="mt-6 w-full"
          onClick={() => navigate(ROUTES.RESET_PASSWORD, { state: { email } })}
        >
          I have a reset code
        </Button>
        <div className="mt-4 text-center text-xs text-text-muted">
          <Link to={ROUTES.LOGIN} className="text-accent hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-7">
        <h1 className="text-xl font-semibold text-text-primary">Reset your password</h1>
        <p className="mt-1 text-[13px] text-text-muted">
          Enter your email and we'll send a reset code.
        </p>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          autoComplete="username"
          leadingIcon={<Mail size={15} />}
        />
        <Button type="submit" variant="primary" loading={isLoading} className="mt-1 w-full">
          Send reset code
        </Button>
      </form>

      <div className="mt-6 text-center text-xs text-text-muted">
        <Link to={ROUTES.LOGIN} className="text-accent hover:underline">
          Back to sign in
        </Link>
      </div>
    </div>
  )
}
